import { useState, useCallback, useRef, useEffect } from "react";
import { Mic, MicOff } from "lucide-react";
import { isSpeechSupported, useSpeechRecognition } from "@/hooks/useSpeechRecognition";

const ERROR_MESSAGES = {
  permission: "Veuillez autoriser l\u2019acc\u00e8s au microphone dans les param\u00e8tres de votre navigateur",
  network: "La dict\u00e9e n\u00e9cessite une connexion internet",
  "not-supported": "La dict\u00e9e vocale n\u2019est pas disponible sur ce navigateur. Utilisez Chrome ou Edge.",
};

export function VoiceButton({ onTranscript, disabled = false, className = "" }) {
  const [errorMsg, setErrorMsg] = useState(null);
  const errorTimer = useRef(null);
  const accumulatedRef = useRef("");

  const handleResult = useCallback(({ finalText }) => {
    if (finalText && onTranscript) {
      accumulatedRef.current += finalText;
      onTranscript(accumulatedRef.current);
    }
  }, [onTranscript]);

  const handleEnd = useCallback(() => {
    accumulatedRef.current = "";
  }, []);

  const { listening, error, toggle, supported } = useSpeechRecognition({
    lang: "fr-FR",
    onResult: handleResult,
    onEnd: handleEnd,
  });

  useEffect(() => {
    if (error) {
      setErrorMsg(ERROR_MESSAGES[error] || "Erreur dictée vocale");
      if (errorTimer.current) clearTimeout(errorTimer.current);
      errorTimer.current = setTimeout(() => setErrorMsg(null), 5000);
    }
    return () => { if (errorTimer.current) clearTimeout(errorTimer.current); };
  }, [error]);

  const isDisabled = disabled || !supported;
  const title = !supported
    ? "La dictée vocale n\u2019est pas disponible sur Firefox. Utilisez Chrome ou Edge."
    : listening
      ? "Cliquez pour arrêter la dictée"
      : "Cliquez pour dicter cette tâche";

  return (
    <div className={`relative inline-flex flex-col items-center ${className}`}>
      <button
        type="button"
        onClick={() => { accumulatedRef.current = ""; toggle(); }}
        disabled={isDisabled}
        title={title}
        className={`
          flex items-center justify-center rounded-lg transition-all
          w-8 h-8 sm:w-8 sm:h-8 min-w-[40px] min-h-[40px] sm:min-w-0 sm:min-h-0
          ${isDisabled
            ? "bg-zinc-800/40 text-zinc-700 cursor-not-allowed"
            : listening
              ? "bg-red-500/20 text-red-400 border border-red-500/50 animate-pulse shadow-[0_0_12px_rgba(239,68,68,0.3)]"
              : "bg-zinc-800/60 text-zinc-400 hover:text-orange-400 hover:bg-zinc-800 border border-transparent hover:border-orange-500/30"
          }
        `}
        data-testid="voice-btn"
      >
        {listening ? <MicOff size={16} /> : <Mic size={16} />}
      </button>
      {errorMsg && (
        <div className="absolute top-full mt-1 left-1/2 -translate-x-1/2 z-50 w-56 bg-zinc-900 border border-red-500/30 rounded-md px-3 py-2 text-[10px] text-red-400 shadow-xl" data-testid="voice-error">
          {errorMsg}
        </div>
      )}
    </div>
  );
}
